<script lang="ts">
import type { ResolvedMonospaceCollection } from '#layers/data-model/public';
import type { CalendarDisplayOptions } from '#layers/listing/types/layout';
import { DisplayTemplateInput, fieldIdToDisplayTemplateAst, getCollectionDefaultDisplayTemplate, getTypename, isPrimitiveTypeAnyDateTime, PrimitiveFieldSelect } from '#layers/data-model/public';

// do not allow time only fields to be start or end field
function guessCollectionStartDateField(collection: ResolvedMonospaceCollection) {
	return collection.primitiveFields.find((field) => isPrimitiveTypeAnyDateTime(field.type) && getTypename(field.type) !== 'time') ?? null;
}

export function getLocaleFirstDayOfWeek(): 0 | 1 {
	try {
		const locale = new Intl.Locale(navigator.language) as any;
		const firstDay: number | undefined = locale.getWeekInfo?.().firstDay ?? locale.weekInfo?.firstDay;

		if (firstDay === undefined) return 0;
		return firstDay === 7 ? 0 : 1;
	}
	catch {
		return 0;
	}
}

export function initializeLayoutCalendarOptions({ collection, options }: { collection: ResolvedMonospaceCollection; options?: Partial<CalendarDisplayOptions> | null }): CalendarDisplayOptions {
	return {
		startDateField: options?.startDateField ?? guessCollectionStartDateField(collection)?.id ?? null,
		endDateField: options?.endDateField ?? null,
		titleTemplate: options?.titleTemplate
			?? (options?.titleField ? fieldIdToDisplayTemplateAst(options.titleField) : null)
			?? getCollectionDefaultDisplayTemplate(collection)
			?? null,
		firstDayOfWeek: options?.firstDayOfWeek ?? 0,
		viewMode: options?.viewMode ?? 'month',
		currentDate: options?.currentDate,
	};
}
</script>

<script setup lang="ts">
import { computed } from 'vue';
import { useInitializedDisplayOptionsModel } from '#layers/listing/composables/use-initialized-display-options-model';

interface CalendarDisplayOptionProps {
	collection: ResolvedMonospaceCollection;
	displayOptions?: CalendarDisplayOptions;
	sharedDisplayOptions?: CalendarDisplayOptions;
}

const props = defineProps<CalendarDisplayOptionProps>();

const emit = defineEmits<{
	'update:displayOptions': [value: CalendarDisplayOptions | undefined];
}>();

const model = useInitializedDisplayOptionsModel(
	{
		baselineDisplayOptions: () => props.sharedDisplayOptions,
		displayOptions: () => props.displayOptions,
		initializedBaselineOptions: () => initializeLayoutCalendarOptions({ collection: props.collection, options: props.sharedDisplayOptions }),
		initializedOptions: () => initializeLayoutCalendarOptions({ collection: props.collection, options: props.displayOptions }),
	},
	(value) => emit('update:displayOptions', value),
);

const collectionDisplayTemplate = computed(() => getCollectionDefaultDisplayTemplate(props.collection));

// do not allow time only fields to be start or end field
function isCalendarDateField(field: { type: any }) {
	return isPrimitiveTypeAnyDateTime(field.type) && getTypename(field.type) !== 'time';
}
</script>

<template>
	<template v-if="model">
		<DFormField :label="$t('layout.calendar.title_field')">
			<DisplayTemplateInput
				v-model="model.titleTemplate"
				:default-value="collectionDisplayTemplate"
				:collection
			/>
		</DFormField>
		<DFormField :label="$t('layout.calendar.start_date_field')">
			<PrimitiveFieldSelect
				v-model="model.startDateField"
				:collection-id="collection.id"
				:filter="isCalendarDateField"
			/>
		</DFormField>
		<DFormField :label="$t('layout.calendar.end_date_field')">
			<PrimitiveFieldSelect
				v-model="model.endDateField"
				:collection-id="collection.id"
				:filter="isCalendarDateField"
				clear
			/>
		</DFormField>
		<DFormSeparator class="-mx-4 my-1" />
		<DFormField :label="$t('layout.calendar.first_day_of_week.label')" layout="inline">
			<div class="flex">
				<DSelect
					v-model="model.firstDayOfWeek"
					class="ms-auto w-fit"
					:items="[
						{ label: $t('layout.calendar.first_day_of_week.options.sunday'), value: 0 },
						{ label: $t('layout.calendar.first_day_of_week.options.monday'), value: 1 },
					]"
				/>
			</div>
		</DFormField>
	</template>
</template>
